import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "./ui/card";
import { toast } from "sonner";
import { Ban, ShieldAlert, RefreshCw, ShieldCheck, ShieldHalf, User as UserIcon } from "lucide-react";
import UserNameLink from "./UserNameLink";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function RoleTag({ role }) {
  if (role === "admin") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-red-500/40 text-red-400 font-mono-num text-[9px] uppercase tracking-widest">
      <ShieldCheck className="w-2.5 h-2.5" /> admin
    </span>
  );
  if (role === "moderator") return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#0D9488]/40 text-[#0D9488] font-mono-num text-[9px] uppercase tracking-widest">
      <ShieldHalf className="w-2.5 h-2.5" /> mod
    </span>
  );
  return null;
}

/**
 * Read-only audit log of every ban / unban decision.
 * Rendered inside AdminSettings as its own tile card. Admin-only endpoint.
 */
export default function BanAuditLogCard() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [nextBefore, setNextBefore] = useState(null);
  const [filter, setFilter] = useState("all"); // all | ban | unban

  const load = useCallback(async (before, currentFilter) => {
    const first = !before;
    if (first) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { limit: 50 };
      if (before) params.before = before;
      if (currentFilter && currentFilter !== "all") params.action = currentFilter;
      const { data } = await api.get("/admin/ban-audit", { params });
      setItems((cur) => first ? data.items : [...cur, ...data.items]);
      setHasMore(!!data.has_more);
      setNextBefore(data.next_before);
      if (first) setTotal(data.total ?? data.items.length);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      if (first) setLoading(false);
      else setLoadingMore(false);
    }
  }, []);

  useEffect(() => { load(null, filter); }, [load, filter]);

  const counts = {
    total,
    bans: items.filter((i) => i.action === "ban").length,
    unbans: items.filter((i) => i.action === "unban").length,
  };

  return (
    <Card className="bg-white border border-slate-200 rounded-none p-6" data-testid="ban-audit-log-card">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Ban className="w-4 h-4 text-[#0D9488]" />
            <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488]">// Moderation history</div>
          </div>
          <h2 className="font-display font-black uppercase tracking-tight text-2xl text-slate-900">Ban Audit Log</h2>
          <p className="text-slate-500 text-xs mt-1">
            Every ban and unban is recorded here with the actor + reason so past decisions can be reviewed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right font-mono-num text-[10px] uppercase tracking-widest text-slate-500 leading-tight">
            <div><span className="text-slate-900 tabular-nums text-sm">{counts.total}</span> total events</div>
            <div className="text-[10px]"><span className="text-orange-400">{counts.bans} bans</span> · <span className="text-emerald-400">{counts.unbans} unbans</span></div>
          </div>
          <button
            type="button"
            onClick={() => load(null, filter)}
            disabled={loading}
            className="w-9 h-9 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-600 flex items-center justify-center transition-colors disabled:opacity-50"
            data-testid="ban-audit-refresh"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 mb-4">
        {[
          { key: "all", label: "All" },
          { key: "ban", label: "Bans" },
          { key: "unban", label: "Unbans" },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={`px-3 h-8 text-[11px] font-mono-num uppercase tracking-widest border transition-colors ${
              filter === f.key
                ? "border-[#0D9488] text-[#0D9488] bg-[#0D9488]/10"
                : "border-slate-200 text-slate-500 hover:border-zinc-600 hover:text-slate-800"
            }`}
            data-testid={`ban-audit-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm font-mono-num animate-pulse" data-testid="ban-audit-loading">Loading history…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-slate-200 p-8 text-center" data-testid="ban-audit-empty">
          <Ban className="w-6 h-6 mx-auto mb-2 text-zinc-700" />
          <div className="font-mono-num text-[10px] uppercase tracking-widest text-slate-500 mb-1">// No records yet</div>
          <div className="text-slate-600 text-sm">
            When staff ban or unban a member, the event will show up here.
          </div>
        </div>
      ) : (
        <>
          <ul className="border border-slate-200 divide-y divide-zinc-900 bg-slate-50/40" data-testid="ban-audit-list">
            {items.map((it) => {
              const isBan = it.action === "ban";
              return (
                <li key={it.id} className="p-4" data-testid={`ban-audit-row-${it.id}`}>
                  <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-8 h-8 border flex items-center justify-center ${
                      isBan
                        ? "border-orange-500/40 text-orange-400 bg-orange-500/5"
                        : "border-emerald-500/40 text-emerald-400 bg-emerald-500/5"
                    }`}>
                      {isBan ? <Ban className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-mono-num text-[10px] uppercase tracking-[0.25em] ${isBan ? "text-orange-400" : "text-emerald-400"}`}>
                          // {isBan ? "Banned" : "Unbanned"}
                        </span>
                        <UserNameLink
                          userId={it.target_user_id}
                          name={it.target_name || it.target_email}
                          className="text-slate-900 font-medium text-sm"
                          testid={`ban-audit-target-${it.id}`}
                        />
                        <RoleTag role={it.target_role} />
                        <span className="text-slate-500 text-xs font-mono-num truncate">{it.target_email}</span>
                        <span className="ml-auto font-mono-num text-[10px] text-slate-400 uppercase tracking-widest whitespace-nowrap">
                          {timeAgo(it.created_at)}
                        </span>
                      </div>
                      <div className="mt-1.5 text-xs font-mono-num text-slate-500 flex items-center gap-1.5 flex-wrap">
                        <UserIcon className="w-3 h-3" /> by <span className="text-slate-800">{it.actor_name}</span>
                        <RoleTag role={it.actor_role} />
                      </div>
                      {isBan && it.reason && (
                        <div className="mt-2 border-l-2 border-orange-500/40 pl-3 text-sm text-slate-800 whitespace-pre-wrap" data-testid={`ban-audit-reason-${it.id}`}>
                          {it.reason}
                        </div>
                      )}
                      {isBan && !it.reason && (
                        <div className="mt-1 text-xs text-slate-400 italic">(no reason provided)</div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          {hasMore && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => load(nextBefore, filter)}
                disabled={loadingMore}
                className="inline-flex items-center gap-2 border border-slate-200 hover:border-[#0D9488] hover:text-[#0D9488] text-slate-600 px-4 h-9 text-[11px] uppercase tracking-widest font-mono-num transition-colors disabled:opacity-50"
                data-testid="ban-audit-load-more"
              >
                {loadingMore ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
